import type { FC, PointerEvent, ReactNode } from 'react'

import { useRef } from 'react'
import Image from 'next/image'
import {
    ArrowRight,
    Gift,
    MonitorSmartphone,
    Palette
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { Sticker } from '@/components/generals/sticker'

const WORDMARK = 'Huzzah'

// Pieces drift with the mouse by different amounts, which reads as depth.
// Keep these as literal class names so the stylesheet picks them up.
const DRIFT = 'transition-[translate] duration-700 ease-out'
const DRIFT_BACK_LARGE = '[translate:calc(var(--mx,0)*-56px)_calc(var(--my,0)*-56px)]'
const DRIFT_BACK = '[translate:calc(var(--mx,0)*-32px)_calc(var(--my,0)*-32px)]'
const DRIFT_FRONT = '[translate:calc(var(--mx,0)*36px)_calc(var(--my,0)*36px)]'
const DRIFT_FRONT_SMALL = '[translate:calc(var(--mx,0)*16px)_calc(var(--my,0)*16px)]'

const PILL = 'absolute hidden items-center gap-2 rounded-full border-2 border-plum px-4 py-2 font-heading text-lg text-plum shadow-[0_4px_0_0_var(--color-plum)] md:inline-flex'

export const Hero: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    const onPointerMove = (event: PointerEvent<HTMLElement>): void => {
        const section = sectionRef.current
        if (!section || event.pointerType !== 'mouse') return

        const rect = section.getBoundingClientRect()
        section.style.setProperty('--mx', ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3))
        section.style.setProperty('--my', ((event.clientY - rect.top) / rect.height - 0.5).toFixed(3))
    }

    const onPointerLeave = (): void => {
        sectionRef.current?.style.setProperty('--mx', '0')
        sectionRef.current?.style.setProperty('--my', '0')
    }

    return (
        <section
            ref={sectionRef}
            id='top'
            aria-labelledby='hero-title'
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
            className='relative overflow-x-clip bg-cream pt-28 sm:pt-32'
        >
            <div className='mx-auto max-w-6xl px-5 text-center sm:px-8'>
                <h1 id='hero-title' className='font-heading text-plum'>
                    <span className='relative mx-auto block w-fit'>
                        <span className='sr-only'>{WORDMARK}</span>

                        <span aria-hidden='true' className='block text-[clamp(5.5rem,24vw,19rem)] leading-[0.8] tracking-[-0.01em] uppercase'>
                            {WORDMARK.split('').map((letter, index) => (
                                <span
                                    key={`${letter}-${index}`}
                                    className='bouncy-letter animate-letter cursor-default'
                                    style={{ animationDelay: `${index * 80}ms` }}
                                >
                                    {letter}
                                </span>
                            ))}
                        </span>

                        <Sticker
                            label='Bold'
                            shape='flower'
                            colorClassName='text-mint'
                            className={cn(
                                'absolute top-[34%] -left-[10%] size-[clamp(3.75rem,10vw,7.5rem)] -rotate-12 text-[clamp(0.95rem,2.3vw,1.7rem)] motion-safe:animate-float',
                                DRIFT,
                                DRIFT_BACK
                            )}
                        />

                        <Sticker
                            label='Playful'
                            shape='blob'
                            colorClassName='text-bubblegum'
                            className={cn(
                                'absolute top-[2%] left-[45%] size-[clamp(3.75rem,10vw,7.5rem)] rotate-6 text-[clamp(0.85rem,2vw,1.45rem)] motion-safe:animate-wiggle',
                                DRIFT,
                                DRIFT_FRONT
                            )}
                        />

                        <Sticker
                            label='Clever'
                            shape='burst'
                            colorClassName='text-lavender'
                            className={cn(
                                'absolute -right-[9%] bottom-[0%] size-[clamp(4.25rem,11vw,8.5rem)] rotate-12 text-[clamp(0.95rem,2.3vw,1.7rem)] motion-safe:animate-float',
                                DRIFT,
                                DRIFT_BACK_LARGE
                            )}
                        />
                    </span>

                    <span className='mt-5 block text-[clamp(2rem,5.4vw,4.4rem)] leading-[1.02] animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500 fill-mode-both'>
                        Branding & websites so good,
                        <br className='hidden sm:block' />
                        {' '}people actually{' '}
                        <span className='relative inline-block text-coral'>
                            cheer.
                            <svg
                                aria-hidden='true'
                                viewBox='0 0 200 20'
                                preserveAspectRatio='none'
                                className='absolute -bottom-1 left-0 h-3 w-full text-butter'
                            >
                                <path
                                    d='M3 14 C 40 4, 80 4, 110 11 S 170 18, 197 6'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='6'
                                    strokeLinecap='round'
                                    pathLength={1}
                                    className='[stroke-dasharray:1] [stroke-dashoffset:1] animate-[draw_0.9s_ease-out_1.2s_forwards]'
                                />
                            </svg>
                        </span>
                    </span>
                </h1>

                <p className='mx-auto mt-6 max-w-xl text-lg leading-relaxed text-plum/75 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-700 fill-mode-both sm:text-xl'>
                    We're a small, senior creative studio helping ambitious brands look sharp, sound like themselves and grow faster.
                </p>

                <div className='mt-9 flex flex-wrap items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-1000 fill-mode-both'>
                    <ButtonLink href='#contact' size='lg' className='group'>
                        Start a project
                        <ArrowRight className='size-5 transition-transform duration-300 group-hover:translate-x-1' aria-hidden='true' />
                    </ButtonLink>

                    <ButtonLink href='#work' size='lg' variant='secondary'>
                        See our work
                    </ButtonLink>
                </div>
            </div>

            <div className='relative mt-14 overflow-hidden py-16 sm:mt-20 sm:py-24'>
                <div aria-hidden='true' className='bg-rings fade-top absolute inset-0 motion-safe:animate-ring-pulse' />

                <div className='relative mx-auto w-[min(78vw,30rem)] animate-in fade-in zoom-in-75 duration-1000 delay-300 fill-mode-both'>
                    <div className={cn('group relative aspect-square overflow-hidden rounded-full border-[6px] border-plum bg-butter-soft shadow-[0_10px_0_0_var(--color-plum)]', DRIFT, DRIFT_FRONT_SMALL)}>
                        <Image
                            src='/images/hero-vibe.jpg'
                            alt="Playful flat lay of pastel packaging, stickers and color swatches"
                            fill
                            preload
                            sizes='(min-width: 640px) 480px, 78vw'
                            className='scale-110 object-cover transition-transform duration-700 ease-out group-hover:scale-125 group-hover:rotate-3'
                        />
                    </div>

                    <span className={cn(PILL, 'top-[8%] -left-[22%] -rotate-6 bg-mint motion-safe:animate-float', DRIFT, DRIFT_BACK)}>
                        <Palette className='size-5' aria-hidden='true' />
                        Brand identity
                    </span>

                    <span className={cn(PILL, 'top-[46%] -right-[26%] rotate-6 bg-lavender motion-safe:animate-float', DRIFT, DRIFT_FRONT)}>
                        <MonitorSmartphone className='size-5' aria-hidden='true' />
                        Websites
                    </span>

                    <span className={cn(PILL, 'bottom-[6%] -left-[18%] rotate-3 bg-peach motion-safe:animate-float', DRIFT, DRIFT_BACK_LARGE)}>
                        <Gift className='size-5' aria-hidden='true' />
                        Packaging
                    </span>

                    <Sticker
                        label='Since 2016'
                        shape='burst'
                        colorClassName='text-butter'
                        className={cn('absolute -top-[4%] -right-[6%] size-24 rotate-12 text-base motion-safe:animate-wiggle sm:size-28 sm:text-lg', DRIFT, DRIFT_FRONT)}
                    />
                </div>
            </div>
        </section>
    )
}
